import React, { useState } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { 
  Building, 
  Users, 
  Shield, 
  BarChart3, 
  Settings, 
  FileText,
  Globe,
  Zap,
  CheckCircle,
  ArrowRight,
  Clock,
  Database,
  Lock,
  Workflow,
  X,
  Calendar,
  Play,
  Video
} from "lucide-react";

const EnterpriseHub = () => {
  const [showTrialModal, setShowTrialModal] = useState(false);
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [showVideoDemo, setShowVideoDemo] = useState(false);
  const [trialData, setTrialData] = useState({
    company_name: "",
    user_email: "",
    team_size: ""
  });
  const [demoData, setDemoData] = useState({
    company_name: "",
    contact_name: "",
    user_email: "",
    phone: "",
    team_size: "",
    preferred_date: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);

  const API = process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL !== 'https://docgen-platform-1.preview.emergentagent.com' 
    ? process.env.REACT_APP_BACKEND_URL 
    : 'http://localhost:8001';

  const handleStartTrial = async () => {
    if (!trialData.company_name || !trialData.user_email) {
      alert('Please fill in company name and email address');
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${API}/api/enterprise/start-trial`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...trialData,
          team_size: parseInt(trialData.team_size) || 1
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        alert(`${data.message}\n\nTrial Details:\n• Duration: ${data.trial_details.duration}\n• Team Size: ${data.trial_details.team_size} users\n• Support: ${data.trial_details.support_level}\n• Features: Unlimited documents, team collaboration, analytics & more`);
        setShowTrialModal(false);
        setTrialData({ company_name: "", user_email: "", team_size: "" });
      } else {
        alert('Error starting enterprise trial. Please try again.');
      }
    } catch (error) {
      console.error('Start trial error:', error);
      alert('Error starting enterprise trial. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleScheduleDemo = async () => {
    if (!demoData.company_name || !demoData.contact_name || !demoData.user_email) {
      alert('Please fill in company name, contact name, and email address');
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${API}/api/enterprise/schedule-demo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(demoData)
      });
      
      if (response.ok) {
        const data = await response.json();
        alert(`${data.message}\n\n${data.next_steps}\n\nDemo will include:\n• Platform walkthrough\n• Team collaboration features\n• Custom branding setup\n• Q&A session`);
        setShowDemoModal(false);
        setDemoData({
          company_name: "",
          contact_name: "",
          user_email: "",
          phone: "",
          team_size: "",
          preferred_date: "",
          message: ""
        });
      } else {
        alert('Error scheduling demo. Please try again.');
      }
    } catch (error) {
      console.error('Schedule demo error:', error);
      alert('Error scheduling demo. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const features = [
    {
      icon: Users,
      title: "Multi-User Collaboration",
      description: "Up to 5 users with role-based access controls and team workspaces"
    },
    {
      icon: FileText,
      title: "Unlimited Document Generation",
      description: "Generate unlimited legal documents across all practice areas"
    },
    {
      icon: BarChart3,
      title: "Advanced Analytics",
      description: "Comprehensive reporting on document usage, costs, and team productivity"
    },
    {
      icon: Database,
      title: "Bulk Processing",
      description: "Process multiple documents simultaneously with batch operations"
    },
    {
      icon: Workflow,
      title: "Digital Signature Workflows",
      description: "Integrated e-signature capabilities with automated routing"
    },
    {
      icon: Globe,
      title: "Government Portal Integration",
      description: "Direct filing capabilities with court systems and government agencies"
    },
    {
      icon: Shield,
      title: "Enterprise Security",
      description: "SOC 2 compliance, SSO integration, and advanced data protection"
    },
    {
      icon: Settings,
      title: "Custom Templates & Branding",
      description: "White-label options with your firm's branding and custom templates"
    }
  ];

  const useCases = [
    {
      title: "Law Firms",
      description: "Streamline document preparation and reduce client costs",
      icon: Building,
      benefits: ["Reduce document prep time by 90%", "Lower client costs", "Increase profit margins"]
    },
    {
      title: "Corporate Legal Teams",
      description: "Handle routine legal matters in-house efficiently",
      icon: Users,
      benefits: ["Reduce outside counsel spend", "Faster turnaround times", "Standardized processes"]
    },
    {
      title: "Financial Services",
      description: "Compliance documentation and regulatory filings",
      icon: BarChart3,
      benefits: ["Ensure regulatory compliance", "Automated reporting", "Risk mitigation"]
    },
    {
      title: "Real Estate Companies",
      description: "Property transactions and lease agreements at scale",
      icon: Building,
      benefits: ["Bulk property processing", "Standardized contracts", "Faster closings"]
    }
  ];

  const pricingFeatures = [
    "Up to 5 users",
    "Unlimited document generation", 
    "Enterprise Hub with comparison tools",
    "Bulk document processing & creation",
    "Advanced cost calculator & analytics",
    "Team collaboration workspace",
    "Digital signature workflows",
    "Government portal integrations",
    "Legal news feed & market insights",
    "Custom templates & branding",
    "Premium support & training",
    "Advanced security & compliance"
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="bg-gradient-to-br from-gray-900 to-gray-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Badge className="mb-6 bg-yellow-500 text-black hover:bg-yellow-600">
              Enterprise Solutions
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Enterprise Hub
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Comprehensive legal document automation for teams and organizations. 
              Scale your legal operations with advanced collaboration tools, analytics, and integrations.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button 
                size="lg"
                onClick={() => setShowTrialModal(true)}
                disabled={loading}
                className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold px-8 py-4"
              >
                {loading ? "Loading..." : "Start Enterprise Trial"}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                variant="outline"
                size="lg"
                onClick={() => setShowDemoModal(true)}
                disabled={loading}
                className="border-white text-white hover:bg-white hover:text-gray-900"
              >
                <Calendar className="mr-2 h-5 w-5" />
                Schedule Demo
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Enterprise Features
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need to manage legal documents at enterprise scale
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow border-gray-200 text-center">
                <CardContent className="p-6">
                  <div className="p-3 bg-yellow-100 rounded-lg w-fit mx-auto mb-4">
                    <feature.icon className="w-8 h-8 text-yellow-600" />
                  </div>
                  <h3 className="font-semibold text-gray-800 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Perfect for Organizations
            </h2>
            <p className="text-xl text-gray-600">
              Trusted by law firms, corporate legal teams, and businesses worldwide
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {useCases.map((useCase, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow border-gray-200">
                <CardHeader>
                  <div className="flex items-center space-x-3">
                    <div className="p-3 bg-yellow-100 rounded-lg">
                      <useCase.icon className="w-6 h-6 text-yellow-600" />
                    </div>
                    <div>
                      <CardTitle className="text-xl font-bold text-gray-800">
                        {useCase.title}
                      </CardTitle>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent>
                  <p className="text-gray-600 mb-4">
                    {useCase.description}
                  </p>
                  
                  <ul className="space-y-2">
                    {useCase.benefits.map((benefit, benefitIndex) => (
                      <li key={benefitIndex} className="flex items-center text-sm text-gray-700">
                        <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="bg-gray-800 py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">
              Enterprise Pricing
            </h2>
            <p className="text-xl text-gray-300">
              Comprehensive solution for teams and organizations
            </p>
          </div>

          <Card className="max-w-2xl mx-auto">
            <CardHeader className="text-center pb-6">
              <Badge className="mb-4 bg-yellow-500 text-black w-fit mx-auto">
                Most Popular
              </Badge>
              <CardTitle className="text-3xl font-bold text-gray-800">
                Enterprise
              </CardTitle>
              <div className="text-4xl font-bold text-gray-800 mt-4">
                $99.99
                <span className="text-lg font-normal text-gray-600">/month</span>
              </div>
              <p className="text-gray-600 mt-2">
                For teams and growing businesses
              </p>
            </CardHeader>
            
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-8">
                {pricingFeatures.map((feature, index) => (
                  <div key={index} className="flex items-center text-sm text-gray-700">
                    <CheckCircle className="w-4 h-4 mr-2 text-green-500 flex-shrink-0" />
                    {feature}
                  </div>
                ))}
              </div>

              <div className="text-center">
                <Button 
                  size="lg"
                  onClick={() => setShowTrialModal(true)}
                  disabled={loading}
                  className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-semibold py-3 mb-4"
                >
                  {loading ? "Loading..." : "Get Started"}
                </Button>
                <p className="text-sm text-gray-600">
                  7-day free trial included • No setup fees • Cancel anytime
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-800 mb-4">
            Ready to Transform Your Legal Operations?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Join hundreds of organizations already saving time and money with FormForgePro Enterprise
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              onClick={() => setShowTrialModal(true)}
              disabled={loading}
              className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold px-8 py-3"
            >
              {loading ? "Loading..." : "Start Free Trial"}
            </Button>
            <Button 
              variant="outline"
              size="lg"
              onClick={() => setShowVideoDemo(true)}
              disabled={loading}
              className="border-teal-600 text-teal-600 hover:bg-teal-50 px-8 py-3"
            >
              <Play className="w-4 h-4 mr-2" />
              Watch Demo
            </Button>
            <Button 
              variant="outline"
              size="lg"
              onClick={() => setShowDemoModal(true)}
              disabled={loading}
              className="border-gray-300 text-gray-700 hover:bg-gray-50 px-8 py-3"
            >
              Contact Sales
            </Button>
          </div>
        </div>
      </section>

      {/* Trial Modal */}
      {showTrialModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900">Start 7-Day Enterprise Trial</h3>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setShowTrialModal(false)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <p className="text-gray-600 mb-4">
              Start your 7-day free enterprise trial with unlimited documents and team collaboration.
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company Name *
                </label>
                <Input
                  type="text"
                  value={trialData.company_name}
                  onChange={(e) => setTrialData({...trialData, company_name: e.target.value})}
                  placeholder="Enter your company name"
                  className="w-full"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <Input
                  type="email"
                  value={trialData.user_email}
                  onChange={(e) => setTrialData({...trialData, user_email: e.target.value})}
                  placeholder="Enter your email address"
                  className="w-full"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Team Size
                </label>
                <Input
                  type="number"
                  value={trialData.team_size}
                  onChange={(e) => setTrialData({...trialData, team_size: e.target.value})}
                  placeholder="Number of team members"
                  className="w-full"
                />
              </div>
              
              <div className="bg-yellow-50 p-3 rounded-lg">
                <h4 className="font-medium text-yellow-800 mb-2">Enterprise Trial Includes:</h4>
                <ul className="text-sm text-yellow-700 space-y-1">
                  <li>• Unlimited document generation</li>
                  <li>• Team collaboration workspace</li>
                  <li>• Advanced analytics and reporting</li>
                  <li>• Priority support</li>
                  <li>• Custom branding options</li>
                </ul>
              </div>
              
              <div className="flex space-x-3 pt-4">
                <Button
                  onClick={handleStartTrial}
                  disabled={loading || !trialData.company_name || !trialData.user_email}
                  className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-black"
                >
                  {loading ? "Starting..." : "Start Trial"}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowTrialModal(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Demo Modal */}
      {showDemoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-gray-900">Schedule Enterprise Demo</h3>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setShowDemoModal(false)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <p className="text-gray-600 mb-4">
                Get a personalized 30-45 minute demo of FormForgePro Enterprise features.
              </p>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name *
                  </label>
                  <Input
                    type="text"
                    value={demoData.company_name}
                    onChange={(e) => setDemoData({...demoData, company_name: e.target.value})}
                    placeholder="Enter your company name"
                    className="w-full"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Your Name *
                  </label>
                  <Input
                    type="text"
                    value={demoData.contact_name}
                    onChange={(e) => setDemoData({...demoData, contact_name: e.target.value})}
                    placeholder="Enter your full name"
                    className="w-full"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <Input
                    type="email"
                    value={demoData.user_email}
                    onChange={(e) => setDemoData({...demoData, user_email: e.target.value})}
                    placeholder="Enter your email address"
                    className="w-full"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <Input
                    type="tel"
                    value={demoData.phone}
                    onChange={(e) => setDemoData({...demoData, phone: e.target.value})}
                    placeholder="Enter your phone number"
                    className="w-full"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Team Size
                  </label>
                  <Input
                    type="text"
                    value={demoData.team_size}
                    onChange={(e) => setDemoData({...demoData, team_size: e.target.value})}
                    placeholder="e.g., 5-10 people"
                    className="w-full"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Preferred Date/Time
                  </label>
                  <Input
                    type="text"
                    value={demoData.preferred_date}
                    onChange={(e) => setDemoData({...demoData, preferred_date: e.target.value})}
                    placeholder="e.g., Next week, Tuesday afternoon"
                    className="w-full"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Notes
                  </label>
                  <Textarea
                    value={demoData.message}
                    onChange={(e) => setDemoData({...demoData, message: e.target.value})}
                    placeholder="Tell us about your specific needs..."
                    rows={3}
                    className="w-full"
                  />
                </div>
                
                <div className="bg-blue-50 p-3 rounded-lg">
                  <h4 className="font-medium text-blue-800 mb-2">Demo Will Include:</h4>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• Platform walkthrough</li>
                    <li>• Team collaboration features</li>
                    <li>• Custom branding setup</li>
                    <li>• Integration capabilities</li>
                    <li>• Q&A session</li>
                  </ul>
                </div>
                
                <div className="flex space-x-3 pt-4">
                  <Button
                    onClick={handleScheduleDemo}
                    disabled={loading || !demoData.company_name || !demoData.contact_name || !demoData.user_email}
                    className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-black"
                  >
                    {loading ? "Scheduling..." : "Schedule Demo"}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowDemoModal(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Video Demo Modal */}
      {showVideoDemo && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full p-6 relative">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-2xl font-bold text-gray-900 flex items-center">
                <Video className="w-6 h-6 mr-2 text-teal-600" />
                Enterprise Demo
              </h3>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setShowVideoDemo(false)}
                className="hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="bg-gray-100 rounded-lg p-4 mb-4">
              <div className="aspect-video bg-black rounded-lg flex items-center justify-center relative overflow-hidden">
                {/* Video Player */}
                <video 
                  controls 
                  className="w-full h-full object-cover"
                  poster="https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=450&fit=crop&crop=center"
                >
                  <source src="/api/enterprise-demo-video" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
                
                {/* Fallback content if video doesn't load */}
                <div className="absolute inset-0 bg-gradient-to-br from-teal-900 to-blue-900 flex items-center justify-center text-white">
                  <div className="text-center">
                    <Video className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <h4 className="text-xl font-semibold mb-2">Enterprise Demo Video</h4>
                    <p className="text-teal-200 max-w-md">
                      See how FormForgePro Enterprise streamlines legal document workflows 
                      for teams and organizations with advanced collaboration features.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="text-center p-4 bg-teal-50 rounded-lg">
                <Users className="w-8 h-8 text-teal-600 mx-auto mb-2" />
                <h4 className="font-semibold text-gray-800">Team Collaboration</h4>
                <p className="text-sm text-gray-600">Real-time document sharing and editing</p>
              </div>
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <Shield className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                <h4 className="font-semibold text-gray-800">Enterprise Security</h4>
                <p className="text-sm text-gray-600">Advanced encryption and compliance</p>
              </div>
              <div className="text-center p-4 bg-yellow-50 rounded-lg">
                <BarChart3 className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
                <h4 className="font-semibold text-gray-800">Analytics Dashboard</h4>
                <p className="text-sm text-gray-600">Track usage and productivity metrics</p>
              </div>
            </div>
            
            <div className="text-center">
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button 
                  size="lg"
                  onClick={() => {
                    setShowVideoDemo(false);
                    setShowTrialModal(true);
                  }}
                  className="bg-teal-600 hover:bg-teal-700 text-white px-8 py-3"
                >
                  Start 7-Day Trial
                </Button>
                <Button 
                  variant="outline"
                  size="lg"
                  onClick={() => {
                    setShowVideoDemo(false);
                    setShowDemoModal(true);
                  }}
                  className="border-gray-300 text-gray-700 hover:bg-gray-50 px-8 py-3"
                >
                  Schedule Live Demo
                </Button>
              </div>
              <p className="text-sm text-gray-500 mt-3">
                7-day free trial • No credit card required • Full enterprise features
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnterpriseHub;